// Rules for the optional dedication page. Client-safe: used by the wizard (live validation) and by
// the create route (authoritative). Stored sanitized; escaped only when rendered (HTML/PDF).
export const DEDICATION_MAX_CHARS = 300;
export const DEDICATION_MAX_LINES = 6;

export type DedicationResult = { ok: true; value: string | null } | { ok: false; error: string };

const CONTROL = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;
// Embedding/override/isolate controls and BOM/ZWSP. LRM/RLM and ZWJ/ZWNJ stay: real scripts need them.
const BIDI_CONTROLS = /[​‪-‮⁦-⁩﻿]/g;
// Line/paragraph separators, C1 controls (incl. NEL), Arabic letter mark, word joiner, tag characters.
const EXTRA_INVISIBLES = /[\u2028\u2029\u0080-\u009F\u061C\u2060\u{E0020}-\u{E007F}]/gu;
const EMOJI = /[\p{Extended_Pictographic}\u{1F1E6}-\u{1F1FF}\u{1F3FB}-\u{1F3FF}️⃣]/gu;
// A ZWJ left behind by a removed emoji sequence (not between two letters/marks).
const STRAY_ZWJ = /(?<![\p{L}\p{M}])‍|‍(?![\p{L}\p{M}])/gu;

export function sanitizeDedication(raw: unknown): DedicationResult {
  if (raw === undefined || raw === null) return { ok: true, value: null };
  if (typeof raw !== 'string') return { ok: false, error: 'The dedication must be text.' };
  const s = raw
    .normalize('NFC')
    .replace(/\r\n?/g, '\n')
    .replace(CONTROL, '')
    .replace(BIDI_CONTROLS, '')
    .replace(EXTRA_INVISIBLES, '')
    .replace(EMOJI, '')
    .replace(STRAY_ZWJ, '')
    .split('\n')
    .map((line) => line.replace(/[ \t ]+/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  if (!s) return { ok: true, value: null };
  if (Array.from(s).length > DEDICATION_MAX_CHARS) {
    return { ok: false, error: `Please keep the dedication under ${DEDICATION_MAX_CHARS} characters.` };
  }
  if (s.split('\n').length > DEDICATION_MAX_LINES) {
    return { ok: false, error: `Please use at most ${DEDICATION_MAX_LINES} lines.` };
  }
  return { ok: true, value: s };
}

// Book languages rendered with Playwright (browser shaping, any script). Must equal
// lib/pdf/script-classifier.js requiresPlaywright() for SUPPORTED_LANGUAGES (pinned by a test).
export const PLAYWRIGHT_BOOK_LANGUAGES: ReadonlySet<string> = new Set(['ar', 'he', 'hi', 'mr', 'pa', 'te', 'ta', 'ml', 'kn']);
const CJK_BOOK_LANGUAGES = new Set(['zh', 'ja', 'ko']);
const COMPLEX = /[֐-׿؀-ۿݐ-ݿऀ-෿฀-࿿က-႟ሀ-፿ក-៿]/;
const CJK = /[ᄀ-ᇿ぀-ヿ㐀-䶿一-鿿가-힯]/;

/** Can the book's PDF engine draw every character of this dedication? */
export function dedicationFitsBook(text: string, languageCode: string): boolean {
  const lang = (languageCode || 'en').toLowerCase().split(/[-_]/)[0];
  if (PLAYWRIGHT_BOOK_LANGUAGES.has(lang)) return true;
  if (COMPLEX.test(text)) return false;
  if (CJK.test(text) && !CJK_BOOK_LANGUAGES.has(lang)) return false;
  return true;
}

/** Every comic PDF is PDFKit (never Playwright): complex scripts are refused for every comic language. */
export function dedicationFitsComic(text: string, languageCode: string): boolean {
  const base = (languageCode || 'en').toLowerCase().split(/[-_]/)[0];
  return PLAYWRIGHT_BOOK_LANGUAGES.has(base) ? dedicationFitsBook(text, 'en') : dedicationFitsBook(text, languageCode);
}
